import { motion } from 'framer-motion';
import Footer from '../components/Footer';

const SeedProductionIrrigationPage = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6 }
    }
  };

  return (
    <div>
      <section className="pt-32 pb-20 bg-gradient-to-br from-tev-green to-tev-green-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Seed Production & Irrigation Services</h1>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto opacity-90">
              Climate-proofed agricultural production and water-efficient solutions
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <motion.div variants={itemVariants} className="relative order-2 md:order-1">
              <img 
                src="/images/smart_irrigation.jpg" 
                alt="Modern Irrigation System" 
                className="w-full h-96 object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent rounded-2xl"></div>
            </motion.div>
            
            <motion.div variants={itemVariants} className="order-1 md:order-2 bg-gradient-to-br from-blue-50 to-cyan-50 p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="bg-blue-600 text-white p-4 rounded-full mr-4">
                  <i className="fas fa-tint text-2xl"></i>
                </div>
                <h3 className="text-3xl font-bold text-gray-900">Smart Irrigation Solutions</h3>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Our service-based irrigation model allows farmers to access water-efficient systems without upfront costs, 
                while receiving expert management and maintenance support.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-xl shadow-lg border border-blue-200">
                  <i className="fas fa-dollar-sign text-blue-500 text-2xl mb-2"></i>
                  <h4 className="font-semibold text-gray-900">No Upfront Investment</h4>
                  <p className="text-sm text-gray-600">Access irrigation systems without capital costs</p>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-lg border border-green-200">
                  <i className="fas fa-tools text-green-500 text-2xl mb-2"></i>
                  <h4 className="font-semibold text-gray-900">Expert Management</h4>
                  <p className="text-sm text-gray-600">Professional maintenance & support</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Seed Production Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <motion.div variants={itemVariants} className="relative order-1 md:order-2">
              <img 
                src="/images/seeds.jpg" 
                alt="Quality Seed Production" 
                className="w-full h-96 object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-green-900/60 to-transparent rounded-2xl"></div>
            </motion.div>
            
            <motion.div variants={itemVariants} className="order-2 md:order-1 bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="bg-green-600 text-white p-4 rounded-full mr-4">
                  <i className="fas fa-seedling text-2xl"></i>
                </div>
                <h3 className="text-3xl font-bold text-gray-900">Quality Seed Production</h3>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                High-quality seed multiplication with improved varieties for maximum yields and 
                better crop performance.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-xl shadow-lg border border-green-200">
                  <i className="fas fa-dna text-green-500 text-2xl mb-2"></i>
                  <h4 className="font-semibold text-gray-900">Genetic Purity</h4>
                  <p className="text-sm text-gray-600">Careful sourcing from research partners</p>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-lg border border-amber-200">
                  <i className="fas fa-certificate text-amber-500 text-2xl mb-2"></i>
                  <h4 className="font-semibold text-gray-900">Certified Quality</h4>
                  <p className="text-sm text-gray-600">National & international standards</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 bg-gradient-to-r from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Why Choose Our Services?</h2>
          </motion.div>
          
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid md:grid-cols-3 gap-8"
          >
            <motion.div variants={itemVariants} className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="text-center">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-full w-20 h-20 mx-auto mb-4">
                  <i className="fas fa-water text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Water Efficiency</h3>
                <p className="text-gray-600">Maximize crop yields with efficient irrigation</p>
              </div>
            </motion.div>
            
            <motion.div variants={itemVariants} className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="text-center">
                <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-full w-20 h-20 mx-auto mb-4">
                  <i className="fas fa-dollar-sign text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Cost Effective</h3>
                <p className="text-gray-600">No upfront investment required</p>
              </div>
            </motion.div>
            
            <motion.div variants={itemVariants} className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="text-center">
                <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white p-6 rounded-full w-20 h-20 mx-auto mb-4">
                  <i className="fas fa-headset text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Expert Support</h3>
                <p className="text-gray-600">Professional management & maintenance</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Certification Badge */}
      <section className="py-16 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-center"
          >
            <div className="inline-flex items-center bg-gradient-to-r from-amber-600 to-orange-600 px-8 py-6 rounded-full shadow-2xl">
              <i className="fas fa-certificate text-white text-3xl mr-4"></i>
              <div className="text-left">
                <h3 className="text-white font-bold text-xl">Licensed & Certified</h3>
                <p className="text-white text-sm">Seed Services Unit (SSU) Certified</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SeedProductionIrrigationPage;
